import { useState } from "react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown, Globe2, MessageCircle, Cpu, Scale, Settings2,
} from "lucide-react";
import RightmoveMappingSection from "./RightmoveMappingSection";
import RightmoveTestingSection from "./RightmoveTestingSection";
import CmsFieldMapOverview from "./CmsFieldMapOverview";
// Re-exported so the CMS tab can render the same component with settings editing enabled.
export { CmsFieldMapOverview };
import VosaSection from "./VosaSection";
import ZooplaMappingSection from "./ZooplaMappingSection";
import ZooplaTestingSection from "./ZooplaTestingSection";
import RendyMappingSection from "./RendyMappingSection";
import RendyTestingSection from "./RendyTestingSection";
import WhatsAppIntegrationTab from "./WhatsAppIntegrationTab";
import EmailSenderCard from "./EmailSenderCard";
import ResendDomainsTab from "./ResendDomainsTab";
import IdealPostcodesTestingSection from "./IdealPostcodesTestingSection";
import ClaudeAiIntegrationSection from "./ClaudeAiIntegrationSection";
import IconGenerationIntegrationSection from "./IconGenerationIntegrationSection";
import LandRegistryIntegrationSection from "./LandRegistryIntegrationSection";
import PlanningApplicationsIntegrationSection from "./PlanningApplicationsIntegrationSection";
import PlanningAppealsIntegrationSection from "./PlanningAppealsIntegrationSection";
import CacheSettingsSection from "./CacheSettingsSection";
import SsoIntegrationSection from "./SsoIntegrationSection";

// A provider's "Fields" (mapping config) and "Testing" (live API calls) used
// to be two separate top-level tabs each — condensed here into one tab per
// provider with a small inner Fields/Testing toggle, so the outer menu goes
// from 10 tabs down to 7.
function ProviderSection({ providerKey, FieldsComponent, TestingComponent }) {
  const [sub, setSub] = useState("fields");
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40" data-testid={`integration-${providerKey}-subtoggle`}>
        <button
          type="button"
          onClick={() => setSub("fields")}
          className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${sub === "fields" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
          data-testid={`integration-${providerKey}-subtoggle-fields`}
        >
          Fields
        </button>
        <button
          type="button"
          onClick={() => setSub("testing")}
          className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${sub === "testing" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
          data-testid={`integration-${providerKey}-subtoggle-testing`}
        >
          Testing
        </button>
      </div>
      {sub === "fields" ? <FieldsComponent /> : <TestingComponent />}
    </div>
  );
}

/**
 * Admin → Integrations tab. Hosts Rightmove, Zoopla, Rendy, Vosa, WhatsApp,
 * Resend, and Ideal Postcodes as individual sections, grouped into dropdown
 * categories (same pattern as the System nav) to avoid a 12-wide
 * horizontally-scrolling tab strip.
 */
const INTEGRATION_NAV_GROUPS = [
  {
    key: "integrations",
    label: "Integrations",
    icon: Globe2,
    items: [
      { id: "rightmove", label: "Rightmove", testid: "tab-integration-rightmove" },
      { id: "zoopla", label: "Zoopla", testid: "tab-integration-zoopla" },
      { id: "vosa", label: "Vosa", testid: "tab-integration-vosa" },
    ],
  },
  {
    key: "comms-media",
    label: "Comms & Media",
    icon: MessageCircle,
    items: [
      { id: "whatsapp", label: "WhatsApp", testid: "tab-integration-whatsapp" },
      { id: "resend", label: "Resend", testid: "tab-integration-resend" },
      { id: "rendy", label: "Rendy", testid: "tab-integration-rendy" },
    ],
  },
  {
    key: "data-ai",
    label: "Data & AI",
    icon: Cpu,
    items: [
      { id: "ideal-postcodes", label: "Ideal Postcodes", testid: "tab-integration-ideal-postcodes" },
      { id: "claude-ai", label: "Claude AI", testid: "tab-integration-claude-ai" },
      { id: "icon-generation", label: "Icon Generation", testid: "tab-integration-icon-generation" },
    ],
  },
  {
    key: "planning",
    label: "Planning",
    icon: Scale,
    items: [
      { id: "planning-applications", label: "Planning Applications", testid: "tab-integration-planning-applications" },
      { id: "planning-appeals", label: "Planning Appeals", testid: "tab-integration-planning-appeals" },
      { id: "land-registry", label: "Land Registry", testid: "tab-integration-land-registry" },
      { id: "response-caching", label: "Cache", testid: "tab-integration-response-caching" },
    ],
  },
  {
    key: "platform",
    label: "Platform",
    icon: Settings2,
    items: [
      { id: "sso", label: "SSO", testid: "tab-integration-sso" },
    ],
  },
];

export default function IntegrationsTab() {
  const [active, setActive] = useState("rightmove");
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Integrations</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Connect and configure third-party portals and services.
        </p>
      </div>
      <div className="border-b">
        <div className="flex gap-2 overflow-x-auto py-1" data-testid="integrations-nav-groups">
          {INTEGRATION_NAV_GROUPS.map((group) => {
            const activeItem = group.items.find((it) => it.id === active);
            const GroupIcon = group.icon;
            return (
              <DropdownMenu key={group.key}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid={`integrations-nav-group-${group.key}`}
                    className={`py-2 px-3 rounded-md border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeItem
                        ? "border-primary text-primary bg-primary/5"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <GroupIcon size={16} />
                    {activeItem ? activeItem.label : group.label}
                    <ChevronDown size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {group.items.map((it) => (
                    <DropdownMenuItem
                      key={it.id}
                      data-testid={it.testid}
                      onClick={() => setActive(it.id)}
                      className={active === it.id ? "bg-accent font-medium" : ""}
                    >
                      {it.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </div>
      </div>
      <Tabs value={active} onValueChange={setActive} className="w-full">
        <TabsContent value="rightmove" className="mt-0">
          <ProviderSection providerKey="rightmove" FieldsComponent={RightmoveMappingSection} TestingComponent={RightmoveTestingSection} />
        </TabsContent>
        <TabsContent value="zoopla" className="mt-0">
          <ProviderSection providerKey="zoopla" FieldsComponent={ZooplaMappingSection} TestingComponent={ZooplaTestingSection} />
        </TabsContent>
        <TabsContent value="rendy" className="mt-0">
          <ProviderSection providerKey="rendy" FieldsComponent={RendyMappingSection} TestingComponent={RendyTestingSection} />
        </TabsContent>
        <TabsContent value="whatsapp" className="mt-0">
          <WhatsAppIntegrationTab />
        </TabsContent>
        <TabsContent value="resend" className="mt-0 space-y-6">
          <EmailSenderCard />
          <ResendDomainsTab />
        </TabsContent>
        <TabsContent value="ideal-postcodes" className="mt-0">
          <IdealPostcodesTestingSection />
        </TabsContent>
        <TabsContent value="vosa" className="mt-0">
          <VosaSection />
        </TabsContent>
        <TabsContent value="claude-ai" className="mt-0">
          <ClaudeAiIntegrationSection />
        </TabsContent>
        <TabsContent value="icon-generation" className="mt-0">
          <IconGenerationIntegrationSection />
        </TabsContent>
        <TabsContent value="land-registry" className="mt-0">
          <LandRegistryIntegrationSection />
        </TabsContent>
        <TabsContent value="planning-applications" className="mt-0">
          <PlanningApplicationsIntegrationSection />
        </TabsContent>
        <TabsContent value="planning-appeals" className="mt-0">
          <PlanningAppealsIntegrationSection />
        </TabsContent>
        <TabsContent value="response-caching" className="mt-0">
          <CacheSettingsSection />
        </TabsContent>
        <TabsContent value="sso" className="mt-0">
          <SsoIntegrationSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
